import React, { useState, useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'ui-components/components/modal';
import { Checkbox } from 'ui-components/components/checkbox';
import { Button } from 'ui-components/components/button';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Pill } from 'ui-components/components/pill';
import { StoreDropDown } from '../../store-dropdown';
import SavedMappingDropdown from '../../saved-mapping-dropdown';
import { linkIDs } from '../../../services/link-service/links';
import HelpArticleLink from '../../help-article-link';

import Global from '../../../store/global-selectors';
import * as Actions from '../../../store/orders/import/orders.import.actions';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';

import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { CountryConfigNames } from '../../../contexts/country-config-context/country-config-enums';
import { useCountryConfig } from '../../../hooks/use-country-config';
import { OrderImportCSV } from '../constants';
import { useOrderCSVImportContext } from '../order-import-context';
import ModalPortal from '../../modal-portal';
import { setStoreGuid } from '../../../store/orders/import/orders.import.actions';

import style from './order-import-modal.module.less';
import messages from './messages';

const OrderImportModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const stores = useSelector(Global.selectSellerActiveManualStores);
  const { isLoading, closeCSVImport, storeDropdownOptions } = useOrderCSVImportContext();
  const mappings = useSelector(Global.selectOrdersImportMappings);
  const mappingName = useSelector(Global.selectOrdersImportMappingName);
  const trackOrderImport = useTrackWorkflow(Workflows.ORDER_IMPORT_FLOW);

  const [useSellerDefaultUnitsOfMeasureOnCsvOrderImport] = useCountryConfig(
    CountryConfigNames.UseSellerDefaultUnitsOfMeasureOnCsvOrderImport,
  );
  const selectedStoreGuid = useSelector(Global.selectOrdersImportSelectedStoreGuid);

  const importSectionHeaderLink = useSellerDefaultUnitsOfMeasureOnCsvOrderImport ? OrderImportCSV.EU : OrderImportCSV.Default;
  const [file, setFile] = useState(null);
  const [createNewMapping, setCreateNewMapping] = useState(true);
  const [useExistingMappings, setUseExistingMappings] = useState(false);
  const [fileHasHeaders, setFileHasHeaders] = useState(true);
  const [fileExtensionError, setFileExtensionError] = useState(null);

  const trackSampleClick = useCallback(() => {
    trackOrderImport(WorkflowSteps.ORDER_IMPORT_DOWNLOAD_SAMPLE_CLICK);
  }, [trackOrderImport]);

  const onSetMappingName = useCallback(
    (name) => {
      dispatch(Actions.setMappingName(name));
    },
    [dispatch],
  );

  const onStoreSelected = useCallback(
    (storeGuid) => {
      dispatch(setStoreGuid(storeGuid));
    },
    [dispatch],
  );

  const onOpenMappingManagerModal = useCallback(() => {
    dispatch(Actions.openOrderImportMappingsManagerModal());
  }, [dispatch]);

  const openMappingsDeleteModal = useCallback(
    (name) => {
      dispatch(Actions.openOrderImportMappingsDeleteModal(name));
    },
    [dispatch],
  );

  useOnComponentMount(() => {
    dispatch(Actions.getMappings());
  });

  useEffect(() => {
    if (mappings.length !== 0) {
      return;
    }
    setUseExistingMappings(false);
    setCreateNewMapping(true);
    // converted from a getDerivedStateFromProps
  }, [mappings, setUseExistingMappings, setCreateNewMapping]);

  const onUpload = useCallback(() => {
    trackOrderImport(WorkflowSteps.ORDER_IMPORT_UPLOAD_CLICK, {
      fileHasHeaders,
    });
    let name = mappingName;
    if (!useExistingMappings) {
      name = '';
    }
    dispatch(Actions.upload(name, fileHasHeaders, file));
  }, [trackOrderImport, fileHasHeaders, mappingName, useExistingMappings, dispatch, file]);

  const onFileSelect = useCallback(() => {
    trackOrderImport(WorkflowSteps.ORDER_IMPORT_SELECT_FILE_CLICK);
    const importFile = document.getElementById('importFile');
    importFile.value = '';
    setFileExtensionError(null);
    importFile.click();
  }, [trackOrderImport, setFileExtensionError]);

  const handleFileUpload = useCallback(
    (event) => {
      const selectedFile = event.target.files[0];
      const allowedExtensions = ['csv', 'txt'];
      if (selectedFile) {
        const uploadedFileExt = selectedFile.name.substr(selectedFile.name.lastIndexOf('.') + 1).toLowerCase();

        if (allowedExtensions.includes(uploadedFileExt)) {
          const fileUploadReader = new FileReader();
          fileUploadReader.onload = () => {
            setFile(selectedFile);
          };
          trackOrderImport(WorkflowSteps.ORDER_IMPORT_FILE_SELECTED, {
            file: selectedFile.name,
          });
          return fileUploadReader.readAsText(selectedFile);
        }
      }
      setFileExtensionError(intl.formatMessage(messages.pleaseSelect));
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [setFile, setFileExtensionError],
  );

  return (
    <ModalPortal onClose={closeCSVImport}>
      <Modal className={style['order-import-modal']} size='medium'>
        <ModalHeader className={style['order-import-modal-header']}>
          <FormattedMessage {...messages.modalTitle} />
        </ModalHeader>
        <ModalBody className={style['order-import-modal-body']}>
          {isLoading && <LoadingIndicator className={style['loading-indicator']} />}
          {!isLoading && (
            <div>
              <div>
                <p className={style['order-import-section-content']}>
                  <FormattedMessage {...messages.firstLineBlurb} />
                </p>
                <p className={style['order-import-section-content']}>
                  <FormattedMessage
                    {...messages.secondLineBlurb}
                    values={{
                      a: (chunks) => <a href='/settings/stores'>{chunks}</a>,
                    }}
                  />
                </p>
                <p className={style['order-import-section-content']}>
                  <FormattedMessage
                    {...messages.thirdLineBlurb}
                    values={{
                      a: (chunks) => <HelpArticleLink articleId={linkIDs.ImportCsvOrders}>{chunks}</HelpArticleLink>,
                    }}
                  />
                </p>
              </div>

              <hr className={style['order-import-section-divider']} />
              <div className={style['order-import-section-wrapper']}>
                <h3 className={style['order-import-section-header']}>
                  <FormattedMessage {...messages.fileToImport} />
                  <span className={style['order-import-section-header-link']} onClick={trackSampleClick}>
                    <a href={importSectionHeaderLink} id={style['order-import-sample-CSV-file-link']}>
                      <FormattedMessage {...messages.downladSampleCSVFile} />
                    </a>
                  </span>
                </h3>
                <div className={style['order-import-select-button']}>
                  <Button onClick={onFileSelect} theme='tertiary'>
                    + <FormattedMessage {...messages.selectFile} />
                  </Button>
                  <input
                    className={style['order-import-input']}
                    id='importFile'
                    name='importFile'
                    onChange={handleFileUpload}
                    type='file'
                  />

                  {file && (
                    <div className={style['order-import-pill']}>
                      <Pill label={file.name} onRemove={() => setFile(null)} theme='in-progress' />
                    </div>
                  )}
                  {fileExtensionError && <span className={style['order-import-file-extension-error']}>{fileExtensionError}</span>}
                </div>
                <Checkbox
                  checked={fileHasHeaders}
                  label={intl.formatMessage(messages.firstLineColumnHeaders)}
                  onChange={(value) => setFileHasHeaders(value)}
                />
              </div>

              <div className={style['order-import-section-wrapper']}>
                <h3 className={style['order-import-section-header']}>
                  <FormattedMessage {...messages.importToStore} />
                </h3>
                <div>
                  {stores.length < 1 && (
                    <div className={style['order-import-no-manual-stores']}>
                      <FormattedMessage
                        {...messages.noManualStores}
                        values={{
                          storeSetupLink: (
                            <Link onClick={closeCSVImport} to='/settings/stores'>
                              Store Setup
                            </Link>
                          ),
                        }}
                      />
                    </div>
                  )}
                  {stores.length >= 1 && (
                    <StoreDropDown
                      className={style['order-import-dropdown-menu']}
                      onStoreSelected={onStoreSelected}
                      selectedStoreGuid={selectedStoreGuid}
                      stores={storeDropdownOptions}
                      togglerClassName={style['order-import-dropdown-menu-button']}
                    />
                  )}
                </div>
              </div>

              <div className={style['order-import-section-wrapper']}>
                <h3 className={style['order-import-section-header']}>
                  <FormattedMessage {...messages.fieldMappings} />
                </h3>
                <div className={style['order-import-mapping-choices']}>
                  <RadioCheckListItem
                    isChecked={createNewMapping}
                    onChecked={() => {
                      setCreateNewMapping(true);
                      setUseExistingMappings(false);
                    }}
                  >
                    <div>
                      <FormattedMessage {...messages.createNewFieldMapping} />
                    </div>
                  </RadioCheckListItem>

                  <RadioCheckListItem
                    isChecked={useExistingMappings}
                    isDisabled={mappings.length === 0}
                    onChecked={() => {
                      setUseExistingMappings(true);
                      setCreateNewMapping(false);
                    }}
                  >
                    <div>
                      <FormattedMessage {...messages.useSavedMapping} />
                    </div>
                  </RadioCheckListItem>
                </div>
              </div>

              {useExistingMappings && mappings.length > 0 && (
                <div className={style['order-import-section-wrapper']}>
                  <h3 className={style['order-import-section-header']}>
                    <FormattedMessage {...messages.savedMappings} />
                  </h3>

                  <div>
                    <SavedMappingDropdown
                      className={style['order-import-dropdown-menu']}
                      mappingName={mappingName}
                      mappings={mappings}
                      onMappingSelected={onSetMappingName}
                      togglerClassName={style['order-import-dropdown-menu-button']}
                    />
                    <div className={style['order-import-dropdown-extra']}>
                      <Button onClick={onOpenMappingManagerModal} theme='link'>
                        <FormattedMessage {...messages.renameMapping} />
                      </Button>
                      &nbsp;|&nbsp;
                      <Button
                        onClick={() => {
                          openMappingsDeleteModal(mappingName);
                        }}
                        theme='link'
                      >
                        <FormattedMessage {...messages.deleteMapping} />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        {!isLoading && (
          <ModalFooter className={style['order-import-modal-footer']}>
            <div className={style['order-import-button-group']}>
              <Button onClick={closeCSVImport} theme='tertiary'>
                <FormattedMessage {...messages.cancelBtn} />
              </Button>
              <Button
                disabled={!file || !selectedStoreGuid || (storeDropdownOptions.length === 1 && storeDropdownOptions[0].disabled)}
                onClick={onUpload}
                theme='primary'
              >
                <FormattedMessage {...messages.uploadBtn} />
              </Button>
            </div>
          </ModalFooter>
        )}
      </Modal>
    </ModalPortal>
  );
};

export default OrderImportModal;
